import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import { AppProvider } from '@/contexts/AppContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import './App.css';
import GoogleCallback from './pages/GoogleCallback';
import GoogleDriveCallback from './pages/GoogleDriveCallback';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center space-y-4">
      <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Lazy load with retry
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error: any) {
      if (error?.message?.includes('Failed to fetch dynamically imported module')) {
        const count = +(sessionStorage.getItem('chunk-reload') || 0);
        if (count < 2) {
          sessionStorage.setItem('chunk-reload', String(count + 1));
          window.location.reload();
        }
      }
      throw error;
    }
  });
};

// Lazy load components
const MalooApp = lazyWithRetry(() => import('@/components/MalooApp'));
const NotFound = lazyWithRetry(() => import('@/pages/NotFound'));
const AcceptInvitationPage = lazyWithRetry(() => import('@/pages/AcceptInvitationPage'));
const FacebookCallback = lazyWithRetry(() => import('@/pages/FacebookCallback'));
const LinkedInCallback = lazyWithRetry(() => import('@/pages/LinkedInCallback'));
const TwitterCallback = lazyWithRetry(() => import('@/pages/TwitterCallback'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage'));
const CanvaReturnPage = lazyWithRetry(() => import ('@/pages/CanvaReturnPage'));
const OAuthConsentPage = lazyWithRetry(() => import('@/pages/OAuthConsentPage'));
// Brand book preview — opens in its own tab, outside the app shell (see
// src/components/brandbook/links.ts). Lazy so the renderer stays out of the entry bundle.
const BrandBookPreviewPage = lazyWithRetry(() => import('@/components/brandbook/BrandBookPreviewPage'));
// Published brand book — /b/:token, readable by anyone with the link, signed in or not.
const PublicBrandBookPage = lazyWithRetry(() => import('@/components/brandbook/PublicBrandBookPage'));


function App() {
  React.useEffect(() => {
    sessionStorage.setItem('chunk-reload', '0');
  }, []);

const basename = import.meta.env.PROD ? '/app' : '/';


return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="maloo-ui-theme">
        <Router basename={basename}>
        <div className="h-screen overflow-hidden bg-background font-sans antialiased">            
          <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* ── Standalone pages — NO AppProvider needed ── */}
                <Route path="/oauth/facebook/callback" element={<FacebookCallback />} />
                <Route path="/oauth/linkedin/callback" element={<LinkedInCallback />} />
                <Route path="/oauth/twitter/callback" element={<TwitterCallback />} />
                <Route path="/oauth/google/callback" element={<GoogleCallback />} />
                <Route path="/oauth/google-drive/callback" element={<GoogleDriveCallback />} />
                <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/canva-return" element={<CanvaReturnPage />} />
                {/* OAuth 2.1 consent screen — full URL is /app/oauth/consent (Router basename="/app").
                    Must match Dashboard → Authentication → OAuth Server → Authorization Path. */}
                <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                {/* Brand book preview — its own tab, no app shell. Reads only; relies on
                    the session this browser already holds. Path: brandbook/links.ts. */}
                <Route path="/brand-book/:brandId" element={<BrandBookPreviewPage />} />
                {/* Published brand book — anyone with the link, signed in or not. Reads only the
                    published snapshot, via the brand-book-public edge function; never the brand record. */}
                <Route path="/b/:token" element={<PublicBrandBookPage />} />

                {/* ── Main app — needs AppProvider ── */}
                <Route
                  path="/*"
                  element={
                    <AppProvider>
                      <MalooApp />
                    </AppProvider>
                  }
                />
              </Routes>
            </Suspense>
            <Toaster />
          </div>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);
}

export default App;